import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { ReactElement, ReactNode } from 'react';
import { useForm } from 'react-hook-form';

import { useStoreActions, useStoreState } from '~/store/hooks';
import { IGetClientRequestPayload } from '~/store/client/types';
import { useApiMutation, useApiRequest } from '~/hooks/api';
import { IDictionaryTypes } from '~/services/api/dictionaries/types';
import { IMedicaidPlan, IMedicaidPlanType } from '~/services/api/medicaidPlan/types';
import Button from '~/ui/components/common/Button';
import Modal from '~/ui/components/common/Modal';
import FormSelect from '~/ui/components/form/FormSelect';
import Checkbox from '~/ui/components/inputs/Checkbox';
import DatePicker from '~/ui/components/inputs/DatePicker';
import Input from '~/ui/components/inputs/Input';

import { IOption } from '~/types';

import MedicaidTypeLabel from './components/MedicaidTypeLabel';
import {
  getDefaultValues,
  IMedicaidPlanFormValues,
  toCreateRequest,
  toUpdateRequest,
} from './formValues';
import { MONTH_PICKER_PROPS, TYPE_LOCKED_TOOLTIP, TYPE_PLACEHOLDER } from './constants';
import { medicaidPlanResolver } from './validate';

import styles from './MedicaidPlanModal.module.scss';

interface IMedicaidPlanModalProps {
  plan: IMedicaidPlan | null;
  hasExpansion: boolean;
  isRecertificationRequired: boolean;
  requestInfo: IGetClientRequestPayload;
  onClose: () => void;
  onSaved: () => void;
}

const MedicaidPlanModal = ({
  plan,
  hasExpansion,
  isRecertificationRequired,
  requestInfo,
  onClose,
  onSaved,
}: IMedicaidPlanModalProps): ReactElement => {
  const isAdding = !plan;
  const canChooseType = isAdding || plan.isInitial;
  const title = isAdding ? 'Add Medicaid Plan' : 'Edit Medicaid Plan';
  const { onCreateMedicaidPlan, onUpdateMedicaidPlan } = useStoreActions(actions => actions.client);
  const { onGetDictionariesByType } = useStoreActions(actions => actions.dictionaries);

  const typeList = useStoreState(
    state => state.dictionaries.availableDictionaries[IDictionaryTypes.MedicaidType]?.list,
  );

  const { isLoading: areTypesLoading } = useApiRequest(
    () =>
      onGetDictionariesByType({
        type: IDictionaryTypes.MedicaidType,
        clinicId: Number(requestInfo.clinicId),
      }),
    { enabled: hasExpansion && canChooseType, initialLoading: true },
  );

  const getTypes = (): IMedicaidPlanType[] => {
    const currentTypes = plan?.medicaidType ? [plan.medicaidType] : [];
    if (!canChooseType) return currentTypes;
    const available = (typeList || []).map(({ id, value, isExpansion = false }) => ({
      id,
      name: value,
      isExpansion,
    }));
    const missingCurrent = currentTypes.filter(({ id }) => !available.some(t => t.id === id));
    return [...available, ...missingCurrent];
  };
  const types = getTypes();
  const typeOptions: IOption[] = types.map(({ id, name }) => ({ value: id, label: name }));
  const renderTypeOption = (option: IOption): ReactNode => {
    const type = types.find(({ id }) => id === option.value);
    return type ? <MedicaidTypeLabel type={type} /> : option.label;
  };

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<IMedicaidPlanFormValues>({
    defaultValues: getDefaultValues(plan, !hasExpansion && isRecertificationRequired),
    resolver: medicaidPlanResolver,
  });

  const { mutate: savePlan } = useApiMutation(
    (submitted: IMedicaidPlanFormValues) =>
      plan
        ? onUpdateMedicaidPlan({
            requestInfo,
            requestPayload: toUpdateRequest(submitted, hasExpansion, plan),
          })
        : onCreateMedicaidPlan({
            requestInfo,
            requestPayload: toCreateRequest(submitted, hasExpansion),
          }),
    {
      notifyMessage: `Medicaid Plan successfully ${isAdding ? 'added' : 'updated'}`,
      onSuccess: () => onSaved(),
      throwOnError: false,
    },
  );

  const recertificationField = (
    <DatePicker
      name="recertificationDate"
      label="Recertification Date"
      {...MONTH_PICKER_PROPS}
      clearable={hasExpansion}
      control={control}
      errors={errors}
    />
  );

  return (
    <Modal open preventOutsideClick maxWidth="sm" fullWidth onClose={onClose}>
      <section className={styles.container}>
        <IconButton aria-label="Close" className={styles.close} onClick={onClose}>
          <CloseIcon />
        </IconButton>
        <h2 className={styles.title}>{title}</h2>
        <form className={styles.form} onSubmit={handleSubmit(savePlan)}>
          {hasExpansion && (
            <Tooltip title={canChooseType ? '' : TYPE_LOCKED_TOOLTIP} placement="top">
              <span>
                <FormSelect
                  control={control}
                  name="medicaidTypeId"
                  label="Medicaid Type"
                  placeholder={TYPE_PLACEHOLDER}
                  options={typeOptions}
                  renderOption={renderTypeOption}
                  disabled={!canChooseType || areTypesLoading}
                  loading={canChooseType && areTypesLoading}
                />
              </span>
            </Tooltip>
          )}
          <Input
            name="medicaidId"
            label="Medicaid ID"
            placeholder="Enter ID"
            register={register}
            errors={errors}
          />
          {hasExpansion ? (
            <>
              <DatePicker
                name="effectiveDate"
                label="Effective Date"
                {...MONTH_PICKER_PROPS}
                clearable
                control={control}
                errors={errors}
              />
              {recertificationField}
            </>
          ) : (
            <>
              <Checkbox
                size="small"
                name="isRecertificationRequired"
                label="Medicaid Recertification Required"
                control={control}
                errors={errors}
              />
              {watch('isRecertificationRequired') && recertificationField}
            </>
          )}
          <div className={styles.actions}>
            <Button type="button" color="primary" variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={isSubmitting}
              isLoading={isSubmitting}
            >
              Save
            </Button>
          </div>
        </form>
      </section>
    </Modal>
  );
};

export default MedicaidPlanModal;
